import axios from 'axios';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDeleteProductionDeclarationMaterialCommodityOrigin } from '@/services/apis/mutations/production-declaration/use-delete-production-declaration-material-commodity-origin';
import type { ProductionDeclarationCommodityOrigin } from '@/types/core/purchase-order/production-declaration-commodity-origin';

type UseProductionDeclarationMaterialCommodityOriginDeleteParams = {
  materialId: string;
  productionDeclarationId: string;
  token?: string;
};

export function useProductionDeclarationMaterialCommodityOriginDelete({
  materialId,
  productionDeclarationId,
  token,
}: UseProductionDeclarationMaterialCommodityOriginDeleteParams) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const { t: tCommon } = useTranslation('common', {
    useSuspense: false,
  });
  const [commodityOriginToDelete, setCommodityOriginToDelete] =
    useState<ProductionDeclarationCommodityOrigin | null>(null);
  const { isPending, mutate: deleteCommodityOrigin } =
    useDeleteProductionDeclarationMaterialCommodityOrigin();

  const openDeleteDialog = useCallback(
    (commodityOrigin: ProductionDeclarationCommodityOrigin) => {
      setCommodityOriginToDelete(commodityOrigin);
    },
    [],
  );

  const handleDeleteDialogOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (isPending || nextOpen) {
        return;
      }

      setCommodityOriginToDelete(null);
    },
    [isPending],
  );

  const handleDelete = useCallback(() => {
    if (!commodityOriginToDelete) {
      return;
    }

    deleteCommodityOrigin(
      {
        commodityOriginId: commodityOriginToDelete.id,
        materialId,
        productionDeclarationId,
        token,
      },
      {
        onSuccess: () => {
          toast.success(t('materialCommodityOrigins.actions.deleteSuccess'));
          setCommodityOriginToDelete(null);
        },
        onError: (error) => {
          const description = axios.isAxiosError(error)
            ? (error.response?.data?.message ??
              error.response?.data?.detail ??
              t('materialCommodityOrigins.actions.deleteError'))
            : t('materialCommodityOrigins.actions.deleteError');

          toast.error(tCommon('error'), {
            description,
          });
        },
      },
    );
  }, [
    commodityOriginToDelete,
    deleteCommodityOrigin,
    materialId,
    productionDeclarationId,
    t,
    tCommon,
    token,
  ]);

  return {
    handleDelete,
    handleDeleteDialogOpenChange,
    isDeleteDialogOpen: commodityOriginToDelete !== null,
    isPending,
    openDeleteDialog,
  };
}
